// The Helium Vault shell (D50), after design/isomorphic/src/layouts/layouts.tsx: the floating
// rail at xl and above, expanded or folded to icons (a drawer below), the sticky header, the
// content column up to 1600 px, and the phone tab bar. Pages that have no header of their own
// get one from their route handle, so a page renders inside the shell whether or not it has
// been redesigned.
import { useEffect } from "react";
import { Outlet, useLocation, useMatches } from "react-router";

import { useT } from "../i18n";
import { cn } from "../ui/cn";
import { PageHeader } from "../ui/PageHeader";
import { BottomTabs } from "./BottomTabs";
import { Header } from "./Header";
import { isRouteHandle, type RouteHandle, SECTIONS } from "./nav";
import { Rail } from "./Rail";
import { RAIL_OFFSET, useRailWidth } from "./railWidth";
import { Toaster } from "./Toaster";

function useCurrentHandle(): RouteHandle {
  const matches = useMatches();
  return [...matches].reverse().map((m) => m.handle).find(isRouteHandle) ?? SECTIONS.home;
}

export default function Shell() {
  const t = useT();
  const handle = useCurrentHandle();
  const { pathname } = useLocation();
  const [railWidth, setRailWidth] = useRailWidth();
  useEffect(() => {
    document.title = `${handle.title} · OpenCloudSecurity`;
  }, [handle.title, t]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <main className="flex min-h-screen flex-grow">
      <Rail width={railWidth} onWidthChange={setRailWidth} />
      <div className={cn("flex w-full flex-col transition-[margin,width] duration-200", RAIL_OFFSET[railWidth])}>
        <Header className="xl:ps-3" />
        <div className="flex flex-grow flex-col px-4 pb-24 pt-2 md:px-5 md:pb-8 lg:px-6 xl:ps-3 3xl:px-8">
          <div className="mx-auto w-full max-w-[1600px]">
            {handle.ownHeader ? null : (
              <PageHeader
                title={handle.title}
                description={handle.subtitle}
                breadcrumb={handle.group ? [{ name: t.shell.pages.home.title, href: "/" }, { name: handle.group }, { name: handle.title }] : undefined}
              />
            )}
            <Outlet />
          </div>
        </div>
      </div>
      <BottomTabs />
      <Toaster />
    </main>
  );
}
